import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const baseURL = process.env.REACT_APP_BASE_URL;

const useValidateUser = () => {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(localStorage.getItem('userId'));
  const [userInfo, setUserInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Track authentication state
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userIdParam = params.get('user');
    const tokenParam = params.get('token');

    if (userIdParam && tokenParam) {
      login(userIdParam, tokenParam);
    } else if (userId && localStorage.getItem('jwtToken')) {
      setIsAuthenticated(true); // Mark as authenticated
      setLoading(false);
    } else {
      setLoading(false);
      redirectToErrorPage();
    }
  }, [location.search, navigate, userId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserInfo(userId); // Fetch user info after authentication
    }
  }, [isAuthenticated, userId]);

  const login = async (userId, token) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(`${baseURL}auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, token,timezone }),
      });
      if (response.ok) {
        const data = await response.json();
        storeUserId(userId);
        localStorage.setItem('jwtToken', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        if (localStorage.getItem("selectedAgency") !== null) {
          localStorage.removeItem("selectedAgency");
        }
        storeSessionExpiry(); // Store session expiry in local storage
        setIsAuthenticated(true); // Mark as authenticated
        setLoading(false);
        clearQueryParams();
      } else {
        redirectToErrorPage();
      }
    } catch (error) {
      redirectToErrorPage();
    }
  };

  const fetchUserInfo = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${baseURL}user/info?id=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include', // Include credentials (cookies, authorization headers)
      });
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data); // Store user info in state
      } else {
        clearSession();
        navigate('/session-expired');
      }
    } catch (error) {
      clearSession();
      navigate('/session-expired');
    }
  }, [navigate]);

  const refreshSession = useCallback(async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await fetch(`${baseURL}auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, refreshToken,timezone}),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('jwtToken', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        storeSessionExpiry(); // Update session expiry
        setIsAuthenticated(true); // Mark as authenticated
      } else {
        clearSession();
        navigate('/session-expired');
      }
    } catch (error) {
      clearSession();
      navigate('/session-expired');
    }
  }, [userId, navigate]);

  const storeUserId = (userId) => {
    localStorage.setItem('userId', userId);
    setUserId(userId);
  };

  const storeSessionExpiry = () => {
    const expiryTime = Date.now() + 4 * 60 * 60 * 1000; // 4 hours
    localStorage.setItem('sessionExpiry', expiryTime);
  };

  const clearSession = () => {
    localStorage.clear();
  };

  const clearQueryParams = () => {
    navigate(location.pathname, { replace: true });
  };

  const redirectToErrorPage = () => {
    navigate('/error');
  };

  return { loading, userId, userInfo, refreshSession };
};

export default useValidateUser;
